import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Configurator from "@/pages/Configurator";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientBilling from "@/pages/ClientBilling";
import ClientPaymentSetup from "@/pages/ClientPaymentSetup";
import CandidateDashboard from "@/pages/CandidateDashboard";
import CandidateOnboardingWizard from "@/pages/CandidateOnboardingWizard";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MagicLinkVerify from "@/pages/MagicLinkVerify";
import CGUPage from "@/pages/CGUPage";
import JobLandingPage from "@/pages/JobLandingPage";
import MetiersHubPage from "@/pages/MetiersHubPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import VerificationsPage from "@/pages/VerificationsPage";
import AssistancePage from "@/pages/AssistancePage";
import MentionsLegalesPage from "@/pages/MentionsLegalesPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CancellationPage from "@/pages/CancellationPage";
import CandidatePublicProfile from "@/pages/CandidatePublicProfile";
import LocalLandingPage from "@/pages/LocalLandingPage";
import InterimComparator from "@/pages/InterimComparator";
import BecomeExtraPage from "@/pages/BecomeExtraPage";
import EntreprisesPage from "@/pages/EntreprisesPage";
import ProfessionnelsPage from "@/pages/ProfessionnelsPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import { ALL_SLUGS } from "@/data/localSeoData";

function ProtectedRoute({ role, children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (!user) {
        // Preserve where the user tried to go so they land back after login
        const next = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/connexion?next=${next}`} replace />;
    }
    if (role && user.role !== role) return <Navigate to="/" replace />;
    return children;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Toaster
                        position="top-right"
                        richColors
                        theme="light"
                        toastOptions={{ style: { fontFamily: "Satoshi" } }}
                    />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/entreprises" element={<EntreprisesPage />} />
                        <Route path="/professionnels" element={<ProfessionnelsPage />} />
                        <Route path="/blog" element={<BlogPage />} />
                        <Route path="/blog/:slug" element={<BlogArticlePage />} />
                        <Route path="/connexion" element={<Login />} />
                        <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
                        <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
                        <Route path="/auth/magic-link" element={<MagicLinkVerify />} />
                        <Route path="/cgu" element={<CGUPage />} />
                        <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
                        <Route path="/verifications" element={<VerificationsPage />} />
                        <Route path="/assistance" element={<AssistancePage />} />
                        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                        <Route path="/confidentialite" element={<PrivacyPage />} />
                        <Route path="/politique-annulation" element={<CancellationPage />} />
                        <Route path="/metiers" element={<MetiersHubPage />} />
                        <Route path="/metiers/:slug" element={<JobLandingPage />} />
                        {/* SEO local landings — 3 villes hubs + 12 combos ville×métier */}
                        {ALL_SLUGS.map((s) => (
                            <Route
                                key={s}
                                path={`/recrutement-${s}`}
                                element={<LocalLandingPage slugProp={s} />}
                            />
                        ))}
                        {/* Feb 2026 SEO wave-1 — no-prefix routes for Lyon focus URLs */}
                        <Route path="/personnel-salon-eurexpo-lyon" element={<LocalLandingPage slugProp="personnel-salon-eurexpo-lyon" />} />
                        <Route path="/renfort-hotel-lyon" element={<LocalLandingPage slugProp="renfort-hotel-lyon" />} />
                        <Route path="/personnel-logistique-lyon" element={<LocalLandingPage slugProp="personnel-logistique-lyon" />} />
                        {/* Feb 2026 SEO wave-2 — GSC-boosted 8 URLs */}
                        <Route path="/extra-restauration-lyon" element={<LocalLandingPage slugProp="extra-restauration-lyon" />} />
                        <Route path="/renfort-restaurant-lyon" element={<LocalLandingPage slugProp="renfort-restaurant-lyon" />} />
                        <Route path="/plongeur-professionnel-traiteur-lyon" element={<LocalLandingPage slugProp="plongeur-professionnel-traiteur-lyon" />} />
                        <Route path="/personnel-palais-congres-lyon" element={<LocalLandingPage slugProp="personnel-palais-congres-lyon" />} />
                        <Route path="/personnel-salon-lyon" element={<LocalLandingPage slugProp="personnel-salon-lyon" />} />
                        <Route path="/personnel-nettoyage-lyon" element={<LocalLandingPage slugProp="personnel-nettoyage-lyon" />} />
                        <Route path="/agent-entretien-lyon" element={<LocalLandingPage slugProp="agent-entretien-lyon" />} />
                        {/* Feb 2026 SEO — cannibalisation fix : /personnel-evenement-lyon dédoublonnait
                            /personnel-evenementiel-lyon (même intention Google). Redirect vers la
                            page canonique. */}
                        <Route path="/personnel-evenement-lyon" element={<Navigate to="/personnel-evenementiel-lyon" replace />} />
                        <Route path="/personnel-evenementiel-lyon" element={<LocalLandingPage slugProp="personnel-evenementiel-lyon" />} />
                        {/* Feb 2026 Sprint 1 — Conversion war machine */}
                        <Route path="/economie-vs-interim-lyon" element={<InterimComparator />} />
                        <Route path="/devenir-extra-serveur-lyon" element={<BecomeExtraPage slugProp="devenir-extra-serveur-lyon" />} />
                        <Route path="/devenir-barman-independant-lyon" element={<BecomeExtraPage slugProp="devenir-barman-independant-lyon" />} />
                        <Route path="/devenir-plongeur-traiteur-lyon" element={<BecomeExtraPage slugProp="devenir-plongeur-traiteur-lyon" />} />
                        {/* Feb 2026 Vague 3 — Cluster NETTOYAGE (4 pages) */}
                        <Route path="/femme-menage-entreprise-lyon" element={<LocalLandingPage slugProp="femme-menage-entreprise-lyon" />} />
                        <Route path="/agent-nettoyage-bureaux-lyon" element={<LocalLandingPage slugProp="agent-nettoyage-bureaux-lyon" />} />
                        <Route path="/nettoyage-apres-evenement-lyon" element={<LocalLandingPage slugProp="nettoyage-apres-evenement-lyon" />} />
                        <Route path="/agent-entretien-copropriete-lyon" element={<LocalLandingPage slugProp="agent-entretien-copropriete-lyon" />} />
                        {/* Feb 2026 Vague 3 — Cluster URGENCE (4 pages) */}
                        <Route path="/remplacement-salarie-absent-lyon" element={<LocalLandingPage slugProp="remplacement-salarie-absent-lyon" />} />
                        <Route path="/personnel-disponible-rapidement-lyon" element={<LocalLandingPage slugProp="personnel-disponible-rapidement-lyon" />} />
                        <Route path="/trouver-serveur-derniere-minute-lyon" element={<LocalLandingPage slugProp="trouver-serveur-derniere-minute-lyon" />} />
                        <Route path="/renfort-personnel-week-end-lyon" element={<LocalLandingPage slugProp="renfort-personnel-week-end-lyon" />} />
                        <Route path="/inscription" element={<Register role="client" />} />
                        <Route path="/candidat/inscription" element={<Register role="candidate" />} />
                        {/* /devis is public — anonymous users can estimate their mission.
                            Authentication is only required at the final "publish" step
                            (see Configurator.jsx guardBeforePublish). */}
                        <Route path="/devis" element={<Configurator />} />
                        <Route path="/paiement/succes" element={<PaymentSuccess />} />
                        <Route path="/paiement/annule" element={<PaymentCancel />} />
                        <Route path="/candidat-public/:id" element={
                            <ProtectedRoute><CandidatePublicProfile /></ProtectedRoute>
                        } />
                        <Route path="/client/moyen-de-paiement" element={
                            <ProtectedRoute role="client"><ClientPaymentSetup /></ProtectedRoute>
                        } />
                        <Route path="/client/facturation" element={
                            <ProtectedRoute role="client"><ClientBilling /></ProtectedRoute>
                        } />
                        <Route path="/client/*" element={
                            <ProtectedRoute role="client"><ClientDashboard /></ProtectedRoute>
                        } />
                        <Route path="/candidat/onboarding" element={
                            <ProtectedRoute role="candidate"><CandidateOnboardingWizard /></ProtectedRoute>
                        } />
                        <Route path="/candidat/*" element={
                            <ProtectedRoute role="candidate"><CandidateDashboard /></ProtectedRoute>
                        } />
                        <Route path="/admin/*" element={
                            <ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>
                        } />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
